import { put } from 'redux-saga/effects';

import { selectTyped, takeEvery } from '@pressreader/appstore';
import { error } from '@pressreader/logger';
import { navigateToAction, selectNavConfig } from '@pressreader/navigation-core';
import { ViewName } from '@pressreader/navigation-types';

import { TAG_URL_PREFIX } from '../constants';
import { getSeoTopicRouteParam, getSeoTopicTagId } from '../tag.navigation';

import { navigateToTagAction } from './actions';
import { selectLegacyTagRouteDisabled, selectSeoTopicRouteEnabled } from './selectors';

function* navigateToTag({ payload }: ReturnType<typeof navigateToTagAction>) {
    const { tagId, slug, locale, articleId } = payload;

    const seoTopicRouteEnabled = yield* selectTyped(selectSeoTopicRouteEnabled);

    let topic = seoTopicRouteEnabled ? getSeoTopicRouteParam(tagId, slug) : null;
    if (seoTopicRouteEnabled && !topic) {
        // Same-tag renavigation without a slug (e.g. a locale change on the topic page) reuses the
        // current url's topic param instead of falling back to the retired category url.
        const navConfig = yield* selectTyped(selectNavConfig);
        const currentTopic = navConfig && 'topic' in navConfig && typeof navConfig.topic === 'string' ? navConfig.topic : null;
        if (currentTopic && getSeoTopicTagId(currentTopic) === String(tagId)) {
            topic = currentTopic;
        }
    }

    if (topic) {
        // Locale scopes the feed via the nav params but stays out of the url: the seotopic route
        // template has no lang/country segments (SEO requirement) and url generation only
        // serializes template params.
        if (locale) {
            yield put(navigateToAction({ viewName: ViewName.Categories, params: { topic, country: locale.country, lang: locale.lang, articleId } }));
        } else {
            yield put(navigateToAction({ viewName: ViewName.Categories, params: { topic, articleId } }));
        }
        return;
    }

    if (seoTopicRouteEnabled && (yield* selectTyped(selectLegacyTagRouteDisabled))) {
        // No route can serve this navigation: the category url is retired and no slug is known.
        // Refusing beats the alternative — a rejected url generation falls back to booting the legacy app.
        error(new Error(`Cannot navigate to tag ${tagId}: legacy tag urls are disabled and no slug is available`));
        return;
    }

    const category = TAG_URL_PREFIX + tagId;
    if (locale) {
        yield put(navigateToAction({ viewName: ViewName.Categories, params: { category, country: locale.country, lang: locale.lang, articleId } }));
    } else {
        yield put(navigateToAction({ viewName: ViewName.Categories, params: { category, articleId } }));
    }
}

export function* saga() {
    yield takeEvery(navigateToTagAction, navigateToTag);
}

export { navigateToTag };
